import { useCallback, useEffect, useState } from "react";
import { useZones } from "./useZones";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, Input, Select } from "../ui";
import { SettingsSection, StatusText } from "./SettingsSection";
import { useSession } from "./session";

interface NotifierInfo {
  id: string;
  fields: { name: string; kind: string; required?: boolean }[];
  linked: boolean;
  /** null when the server side is set up; else an i18n key saying what is missing */
  ready: string | null;
}

interface ChannelRow {
  id: number;
  kind: string;
  label: string;
  config: Record<string, string>;
  enabled: boolean;
}

interface Snapshot {
  notifiers: NotifierInfo[];
  channels: ChannelRow[];
  prefs: {
    quietFrom: string | null;
    quietTo: string | null;
    timeZone: string | null;
    summaryAt: string | null;
    placePauseMin?: number;
    placePauseUntil?: string | null;
  };
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api/internal/notify${path}`, {
    method,
    credentials: "same-origin",
    headers: body === undefined ? {} : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = (await res.json()) as T & { error?: string };
  if (!res.ok) throw new Error(data.error ?? "error.generic");
  return data;
}

/**
 * Settings → Notifications (I0): where Drang may reach this person outside the app (email now; ntfy and
 * Telegram with I3), and quiet hours, during which messages wait and arrive as one digest afterwards.
 * Every person manages their own channels.
 */
export function NotificationSettings() {
  const { t } = useTranslation();
  const [snap, setSnap] = useState<Snapshot | null>(null);
  const [kind, setKind] = useState("");
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [tested, setTested] = useState<Record<number, { ok: boolean; error?: string }>>({});
  const [link, setLink] = useState<{ code: string; bot: string; link: string } | null>(null);
  const isAdmin = useSession((s) => !!s.session?.profile?.isAdmin);
  const zones = useZones();

  const refresh = useCallback(
    () =>
      call<Snapshot>("GET", "").then(
        (s) => {
          setSnap(s);
          setKind((k) => k || s.notifiers[0]?.id || "");
        },
        (e: Error) => setError(e.message),
      ),
    [],
  );
  useEffect(() => {
    void refresh();
  }, [refresh]);
  // while a link code is shown, look for the new channel every few seconds
  useEffect(() => {
    if (!link) return;
    const before = snap?.channels.length ?? 0;
    const id = setInterval(() => {
      void call<Snapshot>("GET", "").then((s) => {
        setSnap(s);
        if (s.channels.length > before) setLink(null);
      });
    }, 3000);
    return () => clearInterval(id);
  }, [link]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!snap) return null;

  const errorText = (code: string) => t(`errors.${code}`, { defaultValue: t("error.generic") });
  const notifier = snap.notifiers.find((n) => n.id === kind);
  const add = async () => {
    try {
      await call("POST", "/channels", { kind, config: fields });
      setFields({});
      setError(null);
      void refresh();
    } catch (e) {
      setError(errorText((e as Error).message));
    }
  };
  const startLink = async () => {
    try {
      setLink(await call<{ code: string; bot: string; link: string }>("POST", "/telegram/link"));
    } catch (e) {
      setError(errorText((e as Error).message));
    }
  };
  const test = async (id: number) => {
    const r = await call<{ ok: boolean; error?: string }>("POST", `/channels/${id}/test`);
    setTested((x) => ({ ...x, [id]: r }));
  };
  const savePrefs = (patch: Partial<Snapshot["prefs"]>) =>
    call<Snapshot>("PUT", "/prefs", { ...snap.prefs, ...patch }).then(setSnap, (e: Error) =>
      setError(errorText(e.message)),
    );

  return (
    <SettingsSection
      id="settings-notify"
      intro={t("notify.hint")}
      summary={t("settings.summary.notify")}
      title={t("notify.title")}
      testId="settings-notify"
    >
      {snap.channels.length === 0 && <p className="text-[13px] text-neutral-500">{t("notify.none")}</p>}
      <ul className="flex flex-col gap-2">
        {snap.channels.map((c) => {
          const r = tested[c.id];
          return (
            <li
              key={c.id}
              className="flex flex-wrap items-center gap-2 rounded-md border border-neutral-200 px-3 py-2 dark:border-neutral-800"
              data-testid="notify-channel"
              data-kind={c.kind}
            >
              <Checkbox
                checked={c.enabled}
                label={t("notify.enabled")}
                onChange={(v) => void call("PATCH", `/channels/${c.id}`, { enabled: v }).then(refresh)}
              />
              <span className="text-[13px] font-medium">
                {t(`notify.kind.${c.kind}`, { defaultValue: c.kind })}
              </span>
              <span className="truncate text-[12.5px] text-neutral-500">
                {c.label || Object.values(c.config).filter(Boolean).join(" · ")}
              </span>
              {r && (
                <StatusText tone={r.ok ? "ok" : "warn"} testId="notify-test-result">
                  {r.ok ? t("notify.testSent") : errorText(r.error ?? "error.generic")}
                </StatusText>
              )}
              <span className="ml-auto flex items-center gap-1">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => void test(c.id)}
                  data-testid="notify-test"
                >
                  {t("notify.test")}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  aria-label={t("notify.removeLabel", {
                    kind: t(`notify.kind.${c.kind}`, { defaultValue: c.kind }),
                  })}
                  onClick={() => void call("DELETE", `/channels/${c.id}`).then(refresh)}
                >
                  {t("common.remove")}
                </Button>
              </span>
            </li>
          );
        })}
      </ul>

      <form
        className="flex flex-wrap items-end gap-2 border-t border-neutral-200 pt-3 dark:border-neutral-800"
        onSubmit={(e) => {
          e.preventDefault();
          void add();
        }}
      >
        <label className="flex flex-col gap-0.5 text-[12px] text-neutral-500">
          {t("notify.addKind")}
          <Select compact value={kind} onChange={(e) => setKind(e.target.value)} data-testid="notify-kind">
            {snap.notifiers.map((n) => (
              <option key={n.id} value={n.id}>
                {t(`notify.kind.${n.id}`, { defaultValue: n.id })}
              </option>
            ))}
          </Select>
        </label>
        {notifier?.linked && !notifier.ready && (
          <div className="flex min-w-[16rem] flex-1 flex-col gap-1 text-[12.5px]">
            {link ? (
              <span data-testid="telegram-code">
                {t("notify.linkSteps", { bot: `@${link.bot}` })}{" "}
                <span className="font-mono text-[14px] font-semibold tracking-[0.12em]">{link.code}</span> ·{" "}
                <a href={link.link} target="_blank" rel="noreferrer" className="text-accent hover:underline">
                  {t("notify.linkOpen")}
                </a>
              </span>
            ) : (
              <Button
                size="sm"
                variant="secondary"
                className="self-start"
                onClick={() => void startLink()}
                data-testid="telegram-link"
              >
                {t("notify.link")}
              </Button>
            )}
          </div>
        )}
        {notifier?.linked && notifier.ready && isAdmin && (
          // the bot token is a server secret: set in Secrets, with the others (UI cleanup U5)
          <a
            href="#settings-secrets"
            className="text-[12.5px] text-accent underline"
            data-testid="telegram-token"
          >
            {t("notify.botTokenInSecrets")}
          </a>
        )}
        {notifier?.fields.map((f) => (
          <label
            key={f.name}
            className="flex min-w-[12rem] flex-1 flex-col gap-0.5 text-[12px] text-neutral-500"
          >
            {t(`notify.field.${f.name}`, { defaultValue: f.name })}
            <Input
              compact
              type={f.kind === "email" ? "email" : "text"}
              value={fields[f.name] ?? ""}
              onChange={(e) => setFields((x) => ({ ...x, [f.name]: e.target.value }))}
              data-testid={`notify-field-${f.name}`}
            />
          </label>
        ))}
        {!notifier?.linked && (
          <Button type="submit" size="sm" variant="secondary" data-testid="notify-add">
            {t("notify.add")}
          </Button>
        )}
      </form>
      {/* on its own line under the row, as the other explanations here */}
      {notifier?.ready && (
        <p className="-mt-1.5 text-[12px]">
          <StatusText tone="warn">{t(notifier.ready)}</StatusText>
        </p>
      )}

      <div className="flex flex-col gap-1 border-t border-neutral-200 pt-3 text-[12.5px] dark:border-neutral-800">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-neutral-500">{t("notify.summary")}</span>
          <Input
            compact
            type="time"
            className="w-[7rem]"
            aria-label={t("notify.summary")}
            value={snap.prefs.summaryAt ?? ""}
            onChange={(e) => void savePrefs({ summaryAt: e.target.value || null })}
            data-testid="notify-summary-at"
          />
        </div>
        <p className="text-[12px] text-neutral-500">{t("notify.summaryHint")}</p>
      </div>
      {/* the person's own time zone for what reaches them (UI cleanup U5); empty: the server's */}
      <div className="flex flex-col gap-1 border-t border-neutral-200 pt-3 text-[12.5px] dark:border-neutral-800">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-neutral-500">{t("notify.timeZone")}</span>
          <Input
            compact
            className="w-[14rem]"
            list="notify-zones"
            aria-label={t("notify.timeZone")}
            defaultValue={snap.prefs.timeZone ?? ""}
            placeholder={Intl.DateTimeFormat().resolvedOptions().timeZone}
            onBlur={(e) =>
              e.target.value.trim() !== (snap.prefs.timeZone ?? "") &&
              void savePrefs({ timeZone: e.target.value.trim() || null })
            }
            data-testid="notify-tz"
          />
          <datalist id="notify-zones">
            {zones.map((z) => (
              <option key={z} value={z} />
            ))}
          </datalist>
        </div>
        <p className="text-[12px] text-neutral-500">{t("notify.timeZoneHint")}</p>
      </div>
      <div className="flex flex-col gap-1 border-t border-neutral-200 pt-3 text-[12.5px] dark:border-neutral-800">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-neutral-500">{t("notify.quiet")}</span>
          <Input
            compact
            type="time"
            className="w-[7rem]"
            aria-label={t("notify.quietFrom")}
            value={snap.prefs.quietFrom ?? ""}
            onChange={(e) => void savePrefs({ quietFrom: e.target.value || null })}
            data-testid="notify-quiet-from"
          />
          <span className="text-neutral-400">–</span>
          <Input
            compact
            type="time"
            className="w-[7rem]"
            aria-label={t("notify.quietTo")}
            value={snap.prefs.quietTo ?? ""}
            onChange={(e) => void savePrefs({ quietTo: e.target.value || null })}
            data-testid="notify-quiet-to"
          />
        </div>
        <p className="text-[12px] text-neutral-500">{t("notify.quietHint")}</p>
      </div>
      <div className="flex flex-col gap-1 border-t border-neutral-200 pt-3 text-[12.5px] dark:border-neutral-800">
        <div className="flex flex-wrap items-center gap-2" data-testid="notify-place-pause">
          <span className="text-neutral-500">{t("notify.placePause")}</span>
          {snap.prefs.placePauseUntil ? (
            <>
              <StatusText tone="warn">
                {t("notify.placePausedUntil", {
                  until: new Date(snap.prefs.placePauseUntil).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  }),
                })}
              </StatusText>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => void call<Snapshot>("POST", "/pause", { minutes: 0 }).then(setSnap)}
              >
                {t("notify.placeResume")}
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void call<Snapshot>("POST", "/pause", {}).then(setSnap)}
              data-testid="notify-place-pause-now"
            >
              {t("notify.placePauseNow", { n: snap.prefs.placePauseMin ?? 60 })}
            </Button>
          )}
          <span className="text-neutral-500">{t("notify.placePauseDefault")}</span>
          <Select
            compact
            aria-label={t("notify.placePauseDefault")}
            value={snap.prefs.placePauseMin ?? 60}
            onChange={(e) => void savePrefs({ placePauseMin: Number(e.target.value) })}
          >
            {[15, 30, 60, 120, 240, 480].map((m) => (
              <option key={m} value={m}>
                {t("notify.minutes", { n: m })}
              </option>
            ))}
          </Select>
        </div>
        <p className="text-[12px] text-neutral-500">{t("notify.placePauseHint")}</p>
      </div>
      {error && (
        <p role="alert" className="text-[12.5px] text-err">
          {error}
        </p>
      )}
    </SettingsSection>
  );
}
